<template>
  <component
    class="relative overflow-hidden"
    :is="link ? nuxtLink : 'div'"
    :to="link ? link.path : undefined"
    :external="link?.external"
    :target="link?.external ? '_blank' : undefined"
    :class="{
      'is-loading': !isImageLoaded,
    }"
  >
    <img
      ref="img"
      class="h-full w-full"
      :loading="isClient ? 'lazy' : undefined"
      :class="imgClasses"
      :style="{
        ...computedStyles,
        objectFit,
        objectPosition,
      }"
      :src="computedSrc"
      :alt="alt"
      @error="handleImageError"
      @load="handleLoad"
    />
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { ImageProps } from "~/types/schemas/common/Atom/image";
import noImgSrc from "~/assets/images/no-photo.png";

const props = defineProps<ImageProps>();

const imageRef = useTemplateRef("img");
const isClient = ref(false);
const isLoading = ref(true);
const imageExists = ref(true);

const nuxtLink = resolveComponent("NuxtLink");

const attrs = useAttrs();

const computedSrc = computed(() => {
  if (!imageExists.value || !props.src) {
    return noImgSrc;
  }

  return props.src;
});

const computedStyles = computed(() => {
  const styleAttrs = attrs.style as Record<string, string>;
  let styles = {} as Record<string, string>;

  if (styleAttrs?.objectFit) styles["objectFit"] = styleAttrs.objectFit;

  if (
    styleAttrs?.width &&
    styleAttrs?.height &&
    styleAttrs?.objectFit === "contain"
  ) {
    if (styleAttrs?.width > styleAttrs?.height) styles["width"] = "fit-content";
    else styles["height"] = "fit-content";
  }

  return styles;
});

const isImageLoaded = computed(
  () => !isLoading.value || imageRef.value?.complete,
);

const handleImageError = () => {
  isLoading.value = false;
  imageExists.value = false;
};

const handleLoad = () => {
  isLoading.value = false;
};

// Enable lazy loading only after hydration
onMounted(() => {
  isClient.value = true;
});
</script>

<style scoped>
.is-loading::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  background: linear-gradient(
    90deg,
    theme("colors.gray.200") 0%,
    theme("colors.gray.100") 50%,
    theme("colors.gray.200") 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
